import {DocumentStatus} from 'core/dictionaries';
import {UserPermissionValue} from 'types';

type SideFilter = 'OUTGOING' | 'INCOMING' | 'BOTH';

type InvoicePermission =
    | 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW'
    | 'MPL_ISEMT_INVOICE_SHIPMENT_PREPARE'
    | 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS'
    | 'MPL_ISEMT_INVOICE_SHIPMENT_CHECK'
    | 'MPL_ISEMT_INVOICE_SHIPMENT_ACCEPT';

interface PermissionRule {
    permission: InvoicePermission;
    sides: SideFilter;
}

type PermissionAction =
    | 'read'
    | 'edit'
    | 'delete'
    | 'download'
    | 'sendToApproval'
    | 'return'
    | 'approve'
    | 'sendToBuyer'
    | 'checkFail'
    | 'checkSuccess'
    | 'accept'
    | 'reject';

const PERMISSION_RULES: Record<
    PermissionAction,
    Partial<Record<DocumentStatus, PermissionRule>>
> = {
    read: {
        [DocumentStatus.DRAFT]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'OUTGOING',
        },
        [DocumentStatus.IN_PROGRESS]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'OUTGOING',
        },
        [DocumentStatus.UNDER_APPROVAL]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'OUTGOING',
        },
        [DocumentStatus.RETURNED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'OUTGOING',
        },
        [DocumentStatus.APPROVED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'OUTGOING',
        },
        [DocumentStatus.PENDING]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'BOTH',
        },
        [DocumentStatus.NEW]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'INCOMING',
        },
        [DocumentStatus.CHECK_SUCCESS]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'BOTH',
        },
        [DocumentStatus.CHECK_FAIL]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'BOTH',
        },
        [DocumentStatus.ACCEPTED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'BOTH',
        },
        [DocumentStatus.REJECTED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_VIEW',
            sides: 'BOTH',
        },
    },
    edit: {
        [DocumentStatus.DRAFT]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PREPARE',
            sides: 'OUTGOING',
        },
        [DocumentStatus.UNDER_APPROVAL]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
        [DocumentStatus.RETURNED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PREPARE',
            sides: 'OUTGOING',
        },
    },
    delete: {
        [DocumentStatus.DRAFT]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
        [DocumentStatus.UNDER_APPROVAL]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
        [DocumentStatus.RETURNED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
    },
    download: {
        [DocumentStatus.APPROVED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
        [DocumentStatus.PENDING]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
    },
    sendToApproval: {
        [DocumentStatus.DRAFT]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PREPARE',
            sides: 'OUTGOING',
        },
        [DocumentStatus.RETURNED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PREPARE',
            sides: 'OUTGOING',
        },
    },
    approve: {
        [DocumentStatus.UNDER_APPROVAL]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
    },
    return: {
        [DocumentStatus.UNDER_APPROVAL]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
        [DocumentStatus.APPROVED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
        [DocumentStatus.PENDING]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
    },
    sendToBuyer: {
        [DocumentStatus.APPROVED]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_PROCESS',
            sides: 'OUTGOING',
        },
    },
    checkSuccess: {
        [DocumentStatus.NEW]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_CHECK',
            sides: 'INCOMING',
        },
    },
    checkFail: {
        [DocumentStatus.NEW]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_CHECK',
            sides: 'INCOMING',
        },
    },
    accept: {
        [DocumentStatus.CHECK_SUCCESS]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_ACCEPT',
            sides: 'INCOMING',
        },
    },
    reject: {
        [DocumentStatus.CHECK_FAIL]: {
            permission: 'MPL_ISEMT_INVOICE_SHIPMENT_ACCEPT',
            sides: 'INCOMING',
        },
    },
};

const hasPermission = (permissions: UserPermissionValue[], permission: InvoicePermission) =>
    permissions.includes(permission) ||
    permissions.includes(`PERMISSION_${permission}` as UserPermissionValue);

export function checkPermission(
    status: DocumentStatus,
    side: 'OUTGOING' | 'INCOMING',
    permissions: UserPermissionValue[],
    action: PermissionAction,
): boolean {
    const rule = PERMISSION_RULES[action][status];
    if (!rule) return false;

    const hasValidSide = rule.sides === 'BOTH' || rule.sides === side;

    return hasValidSide && hasPermission(permissions, rule.permission);
}
